import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import ReactDOMServer from "react-dom/server";
import styled from "styled-components";
import axios from "axios";
import { Formik } from "formik";
import * as Yup from "yup";
import { axiosPath, path } from "@/routing/helpers/router";
import useIsMounted from "@/layout/components/hooks/useIsMounted";
import { AppInput, AppNotificationAlert } from "@/layout/AppComponents";
import Translator from "@/layout/components/helpers/Translator";
import wlLg6 from "@/assets/images/wl-lg6.png";
import AppLink from "@/routing/helpers/AppLink";

export default function RememberPasswordSetScreen() {
  const { rememberPasswordHex } = useParams();
  const navigate = useNavigate();
  const isMounted = useIsMounted();
  const [error, setError] = useState();
  const [validationSchema] = useState(
    Yup.object().shape({
      username: Yup.string().required(
        ReactDOMServer.renderToString(
          <Translator file="user" name="user.required" />,
        ),
      ),
      password: Yup.string()
        .min(
          8,
          ReactDOMServer.renderToString(
            <Translator
              file="admin"
              name="genera.enter-password-minimum-eight-characters"
            />,
          ),
        )
        .required(
          ReactDOMServer.renderToString(
            <Translator file="user" name="user.required" />,
          ),
        ),
      repeatPassword: Yup.string()
        .required(
          ReactDOMServer.renderToString(
            <Translator file="user" name="user.required" />,
          ),
        )
        .test(
          "passwords-match",
          ReactDOMServer.renderToString(
            <Translator file="user" name="user.password.change.bad-repeat" />,
          ),
          function (value) {
            return this.parent.repeatPassword === value;
          },
        ),
    }),
  );

  const handleSave = async (values) => {
    setError();
    return await axios
      .post(axiosPath("api_r_open_rememberPassword_set", true), {
        ...values,
        rememberPasswordHex,
      })
      .then(({ data }) => {
        if (isMounted.current) {
          if (data?.error) {
            setError(data?.error);
          } else {
            AppNotificationAlert({
              title: ReactDOMServer.renderToString(
                <Translator file="admin" name="completed" />,
              ),
              message: ReactDOMServer.renderToString(
                <Translator file="user" name="user.password.reset.ifCorrect" />,
              ),
              type: "success",
              position: "top-right",
              duration: 5000,
            });
            navigate(
              path({
                name: "user_login",
              }),
            );
          }
        }
      })
      .catch(() => {
        AppNotificationAlert({
          title: "Error",
          message: "error",
          type: "danger",
          position: "top-right",
          duration: 3000,
        });
      });
  };

  return (
    <Container>
      <img src={wlLg6} />
      <Formik
        initialValues={{
          username: "",
          password: "",
          repeatPassword: "",
        }}
        validateOnChange={false}
        validateOnBlur={false}
        validationSchema={validationSchema}
        onSubmit={handleSave}
      >
        {({ setFieldValue, handleSubmit, values, errors, isSubmitting }) => (
          <form onSubmit={handleSubmit}>
            <AppInput
              title={ReactDOMServer.renderToString(
                <Translator file="general" name="general.user" />,
              )}
              value={values.username}
              onChange={(v) => setFieldValue("username", v)}
              error={errors.username}
              autoComplete="username"
              autoFocus
            />
            <AppInput
              title={ReactDOMServer.renderToString(
                <Translator file="user" name="user.password.new.setPassword" />,
              )}
              value={values.password}
              onChange={(v) => setFieldValue("password", v)}
              error={errors.password}
              isPassword
            />
            <AppInput
              title={ReactDOMServer.renderToString(
                <Translator
                  file="user"
                  name="user.password.new.repeatPassword"
                />,
              )}
              value={values.repeatPassword}
              onChange={(v) => setFieldValue("repeatPassword", v)}
              error={errors.repeatPassword}
              isPassword
            />
            <button type="submit" className="btn-login" disabled={isSubmitting}>
              <Translator file="user" name="user.password.new.setPassword" />
            </button>
            {error && (
              <div className="alert alert-danger mt-3">
                {error}
                <br />
                <br />
                <AppLink
                  path="user_remember_password"
                  className="text-decoration-none link-primary"
                >
                  <Translator file="user" name="user-click-here-request-new-password-reset" />
                </AppLink>
              </div>
            )}
          </form>
        )}
      </Formik>
    </Container>
  );
}

const Container = styled.div`
  width: 450px;
  margin: 0 auto;
  padding-top: 60px;
  font-size: 14px;

  img {
    display: block;
    margin: 0 auto 20px;
    width: 250px;
  }

  .btn-login {
    border-radius: 3px;
    margin-top: 20px;
    background-color: #003d47;
    border: 1px solid #547895;
    width: 100%;
    padding: 6px;
    color: #fff;
    font-size: 15px;
  }
`;
